import { useMemo, useState } from 'react'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import FilterMenu from '@/components/generals/filter-menu'
import CompanyMark from '@/components/generals/company-mark'
import InitialsAvatar from '@/components/generals/initials-avatar'
import { Input } from '@/components/ui/input'
import { EmptyState, ViewToolbar } from '@/components/generals/view-parts'
import { ownerByName } from '@/constants/companies'
import { indexById } from '@/lib/crm'
import { formatShortDate } from '@/lib/dates'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'
import SearchIcon from '@/assets/images/_common/search.svg'

const ALL = 'all'

export default function ContactsView() {
    const status = useCrmStore(state => state.status)
    const contacts = useCrmStore(state => state.contacts)
    const companies = useCrmStore(state => state.companies)
    const openDialog = useCrmStore(state => state.openDialog)
    const openContact = useCrmStore(state => state.openContact)
    const openDetail = useCrmStore(state => state.openDetail)
    const [query, setQuery] = useState('')
    const [companyFilter, setCompanyFilter] = useState(ALL)

    const companyIndex = useMemo(() => indexById(companies), [companies])
    const companyOptions = useMemo(() => [
        { value: ALL, label: "All Companies" },
        ...[...companies]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(company => ({ value: company.id, label: company.name }))
    ], [companies])

    const needle = query.trim().toLowerCase()
    const visible = contacts.filter(contact => {
        if (companyFilter !== ALL && contact.companyId !== companyFilter) return false
        if (!needle) return true

        const company = contact.companyId ? companyIndex.get(contact.companyId) : undefined

        return [contact.name, contact.title, contact.email, company?.name ?? '']
            .some(value => value.toLowerCase().includes(needle))
    })

    return (
        <>
            <ViewToolbar>
                <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>
                    <div className='relative w-full max-w-[280px]'>
                        <SearchIcon aria-hidden className='text-subtle pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2' />
                        <Input
                            type='search'
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            placeholder="Search name, title, email…"
                            aria-label="Search contacts"
                            className='h-[30px] rounded-full pl-8 text-[12px]'
                        />
                    </div>
                    <FilterMenu label="Company" value={companyFilter} options={companyOptions} onChange={setCompanyFilter} />
                </div>
                <Button variant='primary' size='sm' onClick={() => openDialog('contact')}>
                    <PlusIcon aria-hidden className='size-3' />
                    New Contact
                </Button>
            </ViewToolbar>

            <div className='border-border flex min-h-0 flex-1 flex-col overflow-auto border-t'>
                {visible.length === 0 ? (
                    <EmptyState
                        title={status === 'loading' ? "Loading contacts…" : contacts.length === 0 ? "No contacts yet" : "No contacts match"}
                        description={status === 'loading' ? undefined : contacts.length === 0 ? "Add the people you work with at each account." : "Try a different name or clear the company filter."}
                    />
                ) : (
                    <table className='w-full min-w-[960px] border-collapse text-[14px] leading-none'>
                        <thead className='bg-background sticky top-0 z-10'>
                            <tr className='border-border border-b'>
                                {["Name", "Title", "Company", "Email", "Phone", "Owner", "Last contacted"].map(label => (
                                    <th key={label} scope='col' className='caption-style text-subtle h-[38px] px-4 text-left font-normal whitespace-nowrap'>
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(contact => {
                                const company = contact.companyId ? companyIndex.get(contact.companyId) : undefined
                                const owner = ownerByName(contact.owner)

                                return (
                                    <tr
                                        key={contact.id}
                                        onClick={() => openContact(contact.id)}
                                        className='border-border hover:bg-card/60 cursor-pointer border-b transition-colors duration-150'
                                    >
                                        <td className='px-4 py-2.5'>
                                            <button
                                                type='button'
                                                onClick={event => {
                                                    event.stopPropagation()
                                                    openContact(contact.id)
                                                }}
                                                className='flex cursor-pointer items-center gap-2.5 text-left font-medium whitespace-nowrap'
                                            >
                                                <InitialsAvatar name={contact.name} className='size-7' />
                                                <span data-mdlfy-cms={`contacts:${contact.id}:name`}>
                                                    {contact.name}
                                                </span>
                                            </button>
                                        </td>
                                        <td className='text-soft px-4 py-2.5 whitespace-nowrap'>
                                            <span data-mdlfy-cms={`contacts:${contact.id}:title`}>
                                                {contact.title}
                                            </span>
                                        </td>
                                        <td className='px-4 py-2.5' onClick={event => event.stopPropagation()}>
                                            {company ? (
                                                <button
                                                    type='button'
                                                    onClick={() => openDetail(company.id)}
                                                    className='hover:text-soft flex cursor-pointer items-center gap-2 whitespace-nowrap transition-colors duration-150'
                                                >
                                                    <CompanyMark company={company} className='size-5 rounded-[5px]' />
                                                    <span data-mdlfy-cms={`companies:${company.id}:name`}>
                                                        {company.name}
                                                    </span>
                                                </button>
                                            ) : (
                                                <span className='text-subtle'>None</span>
                                            )}
                                        </td>
                                        <td className='px-4 py-2.5 whitespace-nowrap' onClick={event => event.stopPropagation()}>
                                            {contact.email ? (
                                                <a href={`mailto:${contact.email}`} className='text-soft hover:text-foreground transition-colors duration-150'>
                                                    <span data-mdlfy-cms={`contacts:${contact.id}:email`}>
                                                        {contact.email}
                                                    </span>
                                                </a>
                                            ) : (
                                                <span className='text-subtle'>None</span>
                                            )}
                                        </td>
                                        <td className='text-soft px-4 py-2.5 whitespace-nowrap tabular-nums'>
                                            <span data-mdlfy-cms={`contacts:${contact.id}:phone`}>
                                                {contact.phone}
                                            </span>
                                        </td>
                                        <td className='px-4 py-2.5'>
                                            <span className='flex items-center gap-1.5 whitespace-nowrap'>
                                                <Avatar src={owner.avatar} alt='' />
                                                <span data-mdlfy-cms={`contacts:${contact.id}:owner`}>
                                                    {contact.owner}
                                                </span>
                                            </span>
                                        </td>
                                        <td className='text-soft px-4 py-2.5 whitespace-nowrap tabular-nums'>
                                            {contact.lastContacted ? formatShortDate(contact.lastContacted) : "Never"}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    )
}
